import { SnapshotData } from './interfaces/campaign';

/**
 * Snapshot status, derived on the client from `scheduledAt` / `completedAt`,
 * which redeem-server already sends in the internal campaign response.
 *
 * A snapshot is a block-height cutoff: holders are counted as of `scheduledAt`,
 * and the recording job writes `completedAt` once it has read the chain. The
 * server picks a snapshot up ~30 minutes after its time and, if a run goes
 * quiet, reclaims and retries it on its own every 15 minutes (redeem-server
 * #263). So there is nothing for the merchant to press: we only tell them where
 * it stands, and point at support once a full day has passed.
 */

export type SnapshotState = 'scheduled' | 'recording' | 'delayed' | 'expired' | 'recorded';

/** How long the recording job gets before we call the snapshot delayed. */
export const SNAPSHOT_RECORDING_GRACE_MS = 45 * 60 * 1000;

/** After this long we stop reassuring and point at support. */
export const SNAPSHOT_GIVE_UP_MS = 24 * 60 * 60 * 1000;

/** Where merchants get help. Opens in a new tab: the admin is embedded in Shopify. */
export const SUPPORT_URL = 'https://forum.manifold.xyz';

export type SnapshotStatus = {
  state: SnapshotState;
  /** The moment holders are counted at. */
  scheduledAt: number;
  /** When the snapshot was actually recorded, if it has been. */
  completedAt: number | null;
};

/**
 * Returns null when there is nothing to describe — no snapshot data at all, or
 * data without a scheduled time. Callers fall back to the plain "Yes".
 */
export function computeSnapshotStatus(
  snapshotData: SnapshotData | null | undefined,
  now: number = Date.now()
): SnapshotStatus | null {
  if (!snapshotData || !snapshotData.scheduledAt) return null;

  const { scheduledAt } = snapshotData;
  const completedAt = snapshotData.completedAt ?? null;

  let state: SnapshotState;
  if (completedAt) state = 'recorded';
  else if (now < scheduledAt) state = 'scheduled';
  else if (now - scheduledAt < SNAPSHOT_RECORDING_GRACE_MS) state = 'recording';
  else if (now - scheduledAt < SNAPSHOT_GIVE_UP_MS) state = 'delayed';
  else state = 'expired';

  return { state, scheduledAt, completedAt };
}

/**
 * True when the gate is already taking orders but holders cannot get their
 * discount, because the snapshot it relies on has not been recorded.
 */
export function isSnapshotBlockingGate(
  campaign: { snapshot: boolean; startDate: number | null; snapshotData?: SnapshotData | null },
  now: number = Date.now()
): boolean {
  if (!campaign.snapshot) return false;
  if (!campaign.startDate || campaign.startDate > now) return false;

  const status = computeSnapshotStatus(campaign.snapshotData, now);
  if (!status) return false;

  return status.state !== 'recorded';
}

/** The server requires the snapshot at least this long before the gate starts. */
export const SNAPSHOT_BEFORE_START_MS = 60 * 60 * 1000;

/**
 * True when the merchant may still move the snapshot: it is a snapshot gate and
 * its time is still in the future. The server enforces the same rule.
 */
export function canEditSnapshotTime(
  campaign: { snapshot: boolean; snapshotData?: SnapshotData | null },
  now: number = Date.now()
): boolean {
  if (!campaign.snapshot) return false;
  const status = computeSnapshotStatus(campaign.snapshotData, now);
  return status?.state === 'scheduled';
}

/** The error to show for a new snapshot time, or null when it is fine. */
export function validateSnapshotTime(
  snapshotDate: number | null,
  startDate: number | null,
  now: number = Date.now()
): string | null {
  if (!snapshotDate) return 'Please pick a snapshot time';
  if (snapshotDate <= now) return 'Snapshot time must be in the future';
  if (startDate && snapshotDate + SNAPSHOT_BEFORE_START_MS > startDate) {
    return 'Snapshot time must be at least 60 minutes before the start date';
  }
  return null;
}

/**
 * "Oct 9, 11:25 AM PDT" — always the merchant's own clock, with the zone spelled
 * out so a snapshot time is never ambiguous. `timeZone` is for tests.
 */
export function formatSnapshotTime(timestamp: number, timeZone?: string): string {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    ...(timeZone ? { timeZone } : {}),
  }).format(new Date(timestamp));
}
